import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/free-solid-svg-icons';
import "../../styles/CourseDetailsView.css";

const CourseDetailsView = ({ onClose }) => {
  const [courses, setCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [coursesPerPage] = useState(5);
  const [editingCourse, setEditingCourse] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Predefined course type options
  const courseTypeOptions = [
    'CADD',
    'Pumo Tech IT',
    'Pumo Tech Automation',  
    'Monz Creative School',
    'Certified',  // Added based on the reference image
  ];

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/course`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        
        setCourses(response.data);
        setFilteredCourses(response.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching courses:', err);
        setError('Failed to fetch course details');
        setLoading(false);
      }
    };

    fetchCourses();
  }, []);

  useEffect(() => {
    // Filter courses when searchTerm changes
    const results = courses.filter(course => 
      course.courseName.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredCourses(results);
    setCurrentPage(1); // Reset to first page when search changes
  }, [searchTerm, courses]);

  const handleEdit = (course) => {
    setEditingCourse({...course}); // Create a copy to avoid direct state mutation
  };

  const handleUpdateCourse = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_API_URL}/courses/${editingCourse.id}`, 
        editingCourse, 
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Fetch updated course list to ensure the most recent data
      const updatedResponse = await axios.get(`${process.env.REACT_APP_API_URL}/course`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      // Update courses with the freshly fetched data
      setCourses(updatedResponse.data);
      setEditingCourse(null);

      // Provide feedback
      alert('Course updated successfully');
    } catch (err) {
      console.error('Error updating course:', err);
      alert('Failed to update course');
    }
  };

  const handleDelete = async (courseId) => {
    if (window.confirm('Are you sure you want to delete this course?')) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${process.env.REACT_APP_API_URL}/courses/${courseId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        
        // Fetch updated course list to ensure the most recent data
        const updatedResponse = await axios.get(`${process.env.REACT_APP_API_URL}/course`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        
        // Update courses with the freshly fetched data
        setCourses(updatedResponse.data);
        alert('Course deleted successfully');
      } catch (err) {
        console.error('Error deleting course:', err);
        alert('Failed to delete course');
      }
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  // Pagination logic
  const indexOfLastCourse = currentPage * coursesPerPage;
  const indexOfFirstCourse = indexOfLastCourse - coursesPerPage;
  const currentCourses = filteredCourses.slice(indexOfFirstCourse, indexOfLastCourse);

  // Function to determine which page buttons to show
  const getPageNumbers = () => {
    const totalPages = Math.ceil(filteredCourses.length / coursesPerPage);
    
    if (totalPages <= 8) {
      // If there are 8 or fewer pages, show all page numbers
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    } else {
      // More complex pagination with ellipsis
      let pages = [];
      
      // Always include first page
      pages.push(1);
      
      // Current page and neighbors
      const startPage = Math.max(2, currentPage - 2);
      const endPage = Math.min(totalPages - 1, currentPage + 2);
      
      // Add ellipsis before current page neighborhood if needed
      if (startPage > 2) {
        pages.push('...');
      }
      
      // Add page numbers around current page
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      
      // Add ellipsis after current page neighborhood if needed
      if (endPage < totalPages - 1) {
        pages.push('...');
      }
      
      // Always include last page
      pages.push(totalPages);
      
      return pages;
    }
  };

  const paginate = (pageNumber) => {
    if (typeof pageNumber === 'number') {
      setCurrentPage(pageNumber);
    }
  };

  if (loading) return <div className="loading">Loading course details...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="course-details-modal">
      <div className="course-details-container">
        <div className="form-header1">
          <h2>Course Details</h2>
          <button
            className="close-button1"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        {/* Search Bar - Modified for full width */}
        <div className="search-container">
          <div className="search-input-wrapper">
            <input
              type="text"
              placeholder="Search course name..."
              value={searchTerm}
              onChange={handleSearch}
              className="search-input"
            />
          </div>
        </div>
        
        {/* Edit Form */}
        {editingCourse && (
          <form onSubmit={handleUpdateCourse} className="edit-course-form">
            <h3>Edit Course</h3>
            <div className="form-group1">
              <label>Course Type:</label>
              <select
                value={editingCourse.courseType}
                onChange={(e) => setEditingCourse({...editingCourse, courseType: e.target.value})}
                required
              >
                {courseTypeOptions.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group1">
              <label>Course Name:</label>
              <input
                type="text"
                value={editingCourse.courseName}
                onChange={(e) => setEditingCourse({...editingCourse, courseName: e.target.value})}
                required
              />
            </div>
            <div className="form-group1">
              <label>Course Fees:</label>
              <input
                type="number"
                value={editingCourse.courseFees}
                onChange={(e) => setEditingCourse({...editingCourse, courseFees: e.target.value})}
                required
              />
            </div>
            <div className="form-group1">
              <label>Course Duration (Hours):</label>
              <input
                type="number"
                value={editingCourse.courseDuration}
                onChange={(e) => setEditingCourse({...editingCourse, courseDuration: e.target.value})}
                required
                min="1"
              />
            </div>
            <div className="form-buttons1">
              <button type="submit" className="submit-button1">Update Course</button>
              <button 
                type="button" 
                className="cancel-button1" 
                onClick={() => setEditingCourse(null)}
              >
                Cancel
              </button>
            </div>
          </form>
        )}
        
        <table className="course-details-table">
          <thead>
            <tr>
              <th>Course Type</th>
              <th>Course Name</th>
              <th>Course Fees</th>
              <th>Course Duration</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentCourses.length > 0 ? (
              currentCourses.map((course) => (
                <tr key={course.id}>
                  <td>{course.courseType}</td>
                  <td>{course.courseName}</td>
                  <td>₹{course.courseFees !== null && course.courseFees !== undefined 
                    ? parseFloat(course.courseFees).toFixed(2) 
                    : 'NaN'}</td>
                  <td>{course.courseDuration} Hours</td>
                  <td>
                    <div className="action-buttons1">
                      <button 
                        className="edit-button1" 
                        onClick={() => handleEdit(course)}
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button 
                        className="delete-button1" 
                        onClick={() => handleDelete(course.id)}
                      >
                        x
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="no-courses">No courses found matching your search criteria</td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {filteredCourses.length > 0 && (
          <div className="pagination1">
            <button 
              onClick={() => currentPage > 1 && paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className="pagination-arrow"
            >
              &laquo;
            </button>
            
            {getPageNumbers().map((number, index) => (
              <button 
                key={index} 
                onClick={() => typeof number === 'number' && paginate(number)}
                className={currentPage === number ? 'active' : (number === '...' ? 'ellipsis' : '')}
                disabled={number === '...'}
              >
                {number}
              </button>
            ))}
            
            <button 
              onClick={() => currentPage < Math.ceil(filteredCourses.length / coursesPerPage) && paginate(currentPage + 1)}
              disabled={currentPage === Math.ceil(filteredCourses.length / coursesPerPage)}
              className="pagination-arrow"
            >
              &raquo;
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseDetailsView;